import React, { FunctionComponent } from 'react';
import classNames from 'clsx';
import { Link } from 'react-router-dom';
import { RailTooltip } from './RailTooltip';
import { NavIcon } from '../../Navbar/NavIcon';

type RailProfileProps = {
  avatar?: string;
  name: string;
  planLabel: string;
  collapsed?: boolean;
  onLogout?: () => void;
  /** Con link, el bloque entero abre Ajustes generales. */
  link?: { href: string; active: boolean };
};

type ProfileBoxProps = React.HTMLAttributes<HTMLElement> & { link?: { href: string; active: boolean } };

/** El bloque de identidad: un enlace a Ajustes generales cuando hay `link`, si no un div. */
const ProfileBox = ({ link, children, ...rest }: ProfileBoxProps) =>
  link ? (
    <Link to={link.href} aria-current={link.active ? 'page' : undefined} {...rest}>
      {children}
    </Link>
  ) : (
    <div {...rest}>{children}</div>
  );

/** Fixed identity row with an explicit, keyboard-accessible logout action. */
export const RailProfile: FunctionComponent<RailProfileProps> = ({
  avatar,
  name,
  planLabel,
  collapsed = false,
  onLogout,
  link
}) => {
  const initial = name?.trim().charAt(0).toUpperCase() || 'A';

  const label = link ? `${name} · ${planLabel} · Ajustes generales` : `${name} · ${planLabel}`;

  return (
    <div className={classNames('flex shrink-0', collapsed ? 'flex-col' : 'items-center')}>
      <RailTooltip label={label} enabled={collapsed} className="min-w-0 flex-1">
        <ProfileBox
          link={link}
          aria-label={collapsed || link ? label : undefined}
          role={collapsed && !link ? 'group' : undefined}
          className={classNames(
            'mt-1.5 flex w-full items-center rounded-xl',
            link &&
              'cursor-pointer transition-colors duration-150 hover:bg-frost-brand-wash focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-frost-brand',
            link?.active && 'bg-frost-brand-wash!',
            // Contraído: 10px arriba y abajo como la maqueta; separa el avatar del último ítem de
            // la lista (que se desvanece al desplazar) para que no parezca tapado.
            collapsed
              ? 'justify-center gap-0 px-0 py-2.5'
              : 'gap-[11px] bg-[rgba(var(--f-ink-rgb),0.04)] p-2.5'
          )}
        >
          {avatar ? (
            <img
              src={avatar}
              width={34}
              height={34}
              className="h-[34px] w-[34px] shrink-0 rounded-full object-cover"
              // Anillo blanco y aro azul suave, como en la maqueta.
              style={{ boxShadow: '0 0 0 2px var(--f-card), 0 0 0 3.5px rgba(var(--f-brand-rgb), 0.3)' }}
              alt={name}
            />
          ) : (
            <span
              className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-full text-fs-13 font-semibold text-white"
              style={{
                background: 'linear-gradient(135deg, var(--f-brand) 0%, var(--f-coral) 100%)',
                boxShadow: '0 0 0 2px var(--f-card), 0 0 0 3.5px rgba(var(--f-brand-rgb), 0.3)'
              }}
              aria-hidden
            >
              {initial}
            </span>
          )}
          {!collapsed && (
            <span className="flex min-w-0 flex-1 flex-col leading-[1.15]">
              <span className="block w-full break-words text-fs-13 font-semibold text-frost-ink">{name}</span>
              <span className="block w-full break-words text-fs-11 font-medium text-frost-ink-mid">
                {planLabel}
              </span>
            </span>
          )}
        </ProfileBox>
      </RailTooltip>
      {onLogout && (
        <RailTooltip label="Salir" enabled>
          <button
            type="button"
            aria-label="Salir"
            onClick={onLogout}
            className="grid h-11 w-11 shrink-0 place-items-center rounded-frost-button text-frost-ink-soft hover:bg-frost-coral-wash hover:text-frost-coral-text focus-visible:ring-2 focus-visible:ring-frost-brand"
          >
            <NavIcon name="sign-out" className="text-fs-13" />
          </button>
        </RailTooltip>
      )}
    </div>
  );
};
